import React, { useContext, useEffect, useMemo, useState } from "react";
import { Link, useLocation, useNavigate } from "react-router-dom";
import { FiHeart, FiSearch } from "react-icons/fi";
import productData from "../Datas/productData";
import { CartContext } from "./CartContext";
import { formatCurrency } from "../config/siteConfig";
import "../css/shop.scss";

const Shop = () => {
  const { wishlistItems, toggleWishlist, isWishlisted } = useContext(CartContext);
  const location = useLocation();
  const navigate = useNavigate();
  const params = useMemo(() => new URLSearchParams(location.search), [location.search]);
  const query = params.get("q") || "";
  const category = params.get("category") || "";
  const subcategory = params.get("subcategory") || "";
  const maker = params.get("maker") || "";
  const showNew = params.get("new") === "1";
  const showSaved = params.get("saved") === "1";
  const sort = params.get("sort") || "featured";
  const requestedPage = Math.max(1, Number(params.get("page")) || 1);
  const pageSize = query ? 24 : 48;
  const [searchInput, setSearchInput] = useState(query);
  const [isLoading, setIsLoading] = useState(true);
  const [loadError, setLoadError] = useState(false);

  useEffect(() => {
    setSearchInput(query);
  }, [query]);

  useEffect(() => {
    setIsLoading(true);
    setLoadError(false);
    const timeout = window.setTimeout(() => {
      try {
        if (!Array.isArray(productData)) throw new Error("Invalid product data");
        setIsLoading(false);
      } catch {
        setLoadError(true);
        setIsLoading(false);
      }
    }, 180);
    return () => window.clearTimeout(timeout);
  }, [location.search]);

  const filteredProducts = useMemo(() => {
    if (!Array.isArray(productData)) return [];
    const normalizedQuery = query.trim().toLowerCase();
    const savedIds = new Set(wishlistItems.map(String));

    const products = productData.filter((product) => {
      if (category && product.category !== category) return false;
      if (subcategory && product.subcategory !== subcategory) return false;
      if (maker && product.brand !== maker) return false;
      if (showNew && !product.newArrival) return false;
      if (showSaved && !savedIds.has(String(product.id))) return false;
      if (!normalizedQuery) return true;
      return [
        product.name,
        product.brand,
        product.category,
        product.subcategory,
        product.description,
        ...(product.tags || []),
      ]
        .join(" ")
        .toLowerCase()
        .includes(normalizedQuery);
    });

    return [...products].sort((a, b) => {
      switch (sort) {
        case "price-asc": return a.price - b.price;
        case "price-desc": return b.price - a.price;
        case "title-asc": return a.name.localeCompare(b.name);
        case "title-desc": return b.name.localeCompare(a.name);
        case "oldest": return a.id - b.id;
        case "newest": return Number(b.newArrival) - Number(a.newArrival) || b.id - a.id;
        case "best-selling": return b.reviews - a.reviews;
        default: return Number(b.featured) - Number(a.featured) || a.id - b.id;
      }
    });
  }, [category, maker, query, showNew, showSaved, sort, subcategory, wishlistItems]);

  const totalPages = Math.max(1, Math.ceil(filteredProducts.length / pageSize));
  const currentPage = Math.min(requestedPage, totalPages);
  const visibleProducts = filteredProducts.slice(
    (currentPage - 1) * pageSize,
    currentPage * pageSize
  );

  const setParam = (key, value, options = {}) => {
    const next = new URLSearchParams(location.search);
    if (value) next.set(key, value);
    else next.delete(key);
    if (!options.keepPage) next.delete("page");
    navigate(`/shop${next.toString() ? `?${next.toString()}` : ""}`);
  };

  const submitSearch = (event) => {
    event.preventDefault();
    setParam("q", searchInput.trim());
  };

  const heading = showSaved
    ? "Saved stationery"
    : showNew
      ? "New arrivals"
      : maker || subcategory || category || (query ? `Search: “${query}”` : "All stationery");

  const description = showSaved
    ? "The pieces you marked for later, kept on this device."
    : query
      ? `${filteredProducts.length} result${filteredProducts.length === 1 ? "" : "s"} across our collection.`
      : "Considered tools for writing, planning, drawing, and a more thoughtful desk.";

  if (!Array.isArray(productData) || loadError) {
    return (
      <main className="shop-page shop-page--message">
        <h1>We couldn&apos;t load the collection.</h1>
        <p>Please refresh the page and try again.</p>
        <button
          type="button"
          onClick={() => {
            setLoadError(false);
            setIsLoading(true);
            window.setTimeout(() => setIsLoading(false), 180);
          }}
        >
          Try again
        </button>
      </main>
    );
  }

  return (
    <main className="shop-page" id="main-content">
      <header className="shop-page__header ink-container">
        <p className="shop-page__eyebrow">Shop the collection</p>
        <h1>{heading}</h1>
        <p>{description}</p>
      </header>

      <div className={`shop-toolbar ink-container ${query ? "shop-toolbar--search" : ""}`}>
        {query && (
          <form className="shop-toolbar__search" onSubmit={submitSearch} role="search">
            <label className="visually-hidden" htmlFor="shop-search">Search products</label>
            <FiSearch aria-hidden="true" />
            <input
              id="shop-search"
              type="search"
              value={searchInput}
              onChange={(event) => setSearchInput(event.target.value)}
              placeholder="Search again"
            />
            <button type="submit">Search</button>
          </form>
        )}

        <label className="shop-toolbar__sort">
          <span>Sort by</span>
          <select value={sort} onChange={(event) => setParam("sort", event.target.value)}>
            <option value="featured">Featured</option>
            <option value="best-selling">Best selling</option>
            <option value="price-asc">Price, low to high</option>
            <option value="price-desc">Price, high to low</option>
            <option value="title-asc">Title, A–Z</option>
            <option value="title-desc">Title, Z–A</option>
            <option value="oldest">Date, old to new</option>
            <option value="newest">Date, new to old</option>
          </select>
        </label>
      </div>

      {(subcategory || maker || showNew || showSaved || query) && (
        <div className="shop-active-filter ink-container">
          <span>{heading}</span>
          <Link to="/shop">Clear filters</Link>
        </div>
      )}

      {isLoading ? (
        <div className="shop-grid ink-container" aria-label="Loading products" aria-busy="true">
          {Array.from({ length: 8 }, (_, index) => (
            <div className="shop-card shop-card--skeleton" key={index} aria-hidden="true">
              <span /><span /><span />
            </div>
          ))}
        </div>
      ) : visibleProducts.length ? (
        <>
          <section className="shop-grid ink-container" aria-label="Products">
            {visibleProducts.map((product) => {
              const saved = isWishlisted(product.id);
              return (
                <article className="shop-card" key={product.id}>
                  <div className="shop-card__media">
                    <Link to={`/shopDetails/${product.id}`} aria-label={`View ${product.name}`}>
                      <img src={product.images[0]} alt={product.name} loading="lazy" />
                      <img src={product.images[1]} alt="" loading="lazy" aria-hidden="true" />
                    </Link>
                    <button
                      type="button"
                      className={saved ? "is-saved" : ""}
                      onClick={() => toggleWishlist(product.id)}
                      aria-label={saved ? `Remove ${product.name} from saved items` : `Save ${product.name}`}
                      aria-pressed={saved}
                    >
                      <FiHeart aria-hidden="true" />
                    </button>
                    {product.stock === 0 && (
                      <span className="shop-card__badge">Out of stock</span>
                    )}
                  </div>
                  <div className="shop-card__info">
                    <h2><Link to={`/shopDetails/${product.id}`}>{product.name}</Link></h2>
                    <p>{formatCurrency(product.price)}</p>
                  </div>
                </article>
              );
            })}
          </section>

          {totalPages > 1 && (
            <nav className="shop-pagination ink-container" aria-label="Product pages">
              <button
                type="button"
                disabled={currentPage === 1}
                onClick={() => setParam("page", String(currentPage - 1), { keepPage: true })}
              >
                Previous
              </button>
              {Array.from({ length: totalPages }, (_, index) => index + 1).map((page) => (
                <button
                  type="button"
                  key={page}
                  className={page === currentPage ? "is-active" : ""}
                  onClick={() => setParam("page", String(page), { keepPage: true })}
                  aria-current={page === currentPage ? "page" : undefined}
                >
                  {page}
                </button>
              ))}
              <button
                type="button"
                disabled={currentPage === totalPages}
                onClick={() => setParam("page", String(currentPage + 1), { keepPage: true })}
              >
                Next
              </button>
            </nav>
          )}
        </>
      ) : (
        <section className="shop-empty ink-container" aria-live="polite">
          <h2>{showSaved ? "Nothing saved yet." : "No matching stationery."}</h2>
          <p>
            {showSaved
              ? "Use the heart on any product to keep it here for later."
              : "Try a broader search or clear the current filters."}
          </p>
          <Link to="/shop">View the full collection</Link>
        </section>
      )}
    </main>
  );
};

export default Shop;
